import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { FtuCsvImportFlow } from './ftu-csv-import-flow';
import { ImportType } from '../../../../../../components/csv-import/data/types/import-type';
import { ImportProvider } from '../../../../../../components/csv-import/ui/components/import-context';
import { getTranslationFn } from '../../../../../../components/csv-import/ui/utils';
import { useOrderCSVImportContext } from '../../../../../../components/order-import/order-import-context';
import { orderImportFieldTranslations } from '../../../../../../components/order-import/order-import-drawer/messages';
import { useProtectedSyncInventory } from '../../../../../../pages/settings/inventory-sources/use-protected-sync-inventory';
import { CSV_ORDER_IMPORT_JOB_COMPLETE } from '../../../../../../services/pusher-service/pusher-contract-messages';
import Globals from '../../../../../../store/global-selectors';
import { Workflows } from '../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';

import type { ImportReadyCallbacks } from './types';

const translateField = getTranslationFn(orderImportFieldTranslations, ImportType.order);

type SimplifiedOrderUploadWrapperProps = {
  onCsvFileChange?: (hasCsvFile: boolean) => void;
  onImportReadyChange?: (importReady: ImportReadyCallbacks | null) => void;
};

export const SimplifiedOrderUploadWrapper = ({ onCsvFileChange, onImportReadyChange }: SimplifiedOrderUploadWrapperProps) => {
  const orderCSVContext = useOrderCSVImportContext();
  const trackProcess = useTrackWorkflow(Workflows.CSV_ORDER_IMPORT_FLOW);
  const selectedStoreGuid = useSelector(Globals.selectOrdersImportSelectedStoreGuid);
  const protectSyncInventory = useProtectedSyncInventory();

  const onCloseCallback = useCallback(() => {
    trackProcess(WorkflowSteps.CANCEL, { step: 'config' });
    orderCSVContext.closeCSVImport();
  }, [orderCSVContext, trackProcess]);

  const createCustomImportSettings = useCallback(
    () => ({
      storeGuid: selectedStoreGuid ?? '',
    }),
    [selectedStoreGuid],
  );

  return (
    <ImportProvider
      createCustomImportSettings={createCustomImportSettings}
      importType={ImportType.order}
      onClose={onCloseCallback}
      onComplete={protectSyncInventory}
      pusherEvent={CSV_ORDER_IMPORT_JOB_COMPLETE}
      trackProcess={trackProcess}
      translateField={translateField}
      workflowType={Workflows.CSV_ORDER_IMPORT_FLOW}
    >
      <FtuCsvImportFlow onCsvFileChange={onCsvFileChange} onImportReadyChange={onImportReadyChange} />
    </ImportProvider>
  );
};
